import { CandidateEditableProfileSchema } from "@artha/api-contracts";
import { CANDIDATE_PROFILE_DRAFT_STORAGE_PREFIXES } from "../personalization.constants";
import type {
  CandidateProfileReviewDraft,
  CandidateProfileSource,
} from "../personalization.types";

const draftCache = new Map<string, CandidateProfileReviewDraft>();

export function readCandidateProfileDraft(
  username: string,
  source: CandidateProfileSource,
): CandidateProfileReviewDraft | null {
  if (typeof window === "undefined") return null;
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(
      CANDIDATE_PROFILE_DRAFT_STORAGE_PREFIXES[source] + username,
    );
  } catch {
    return (
      draftCache.get(
        CANDIDATE_PROFILE_DRAFT_STORAGE_PREFIXES[source] + username,
      ) || null
    );
  }
  try {
    if (!raw) return null;
    const draft = JSON.parse(
      raw,
    ) as Partial<CandidateProfileReviewDraft> | null;
    const profile = CandidateEditableProfileSchema.safeParse(
      draft?.editableProfile,
    );
    if (
      !draft ||
      typeof draft.externalId !== "string" ||
      !draft.externalId ||
      !profile.success
    )
      return null;
    return {
      externalId: draft.externalId,
      ...(typeof draft.ownerExternalId === "string" ? { ownerExternalId: draft.ownerExternalId } : {}),
      editableProfile: profile.data,
      ...(source === "talent_network" && {
        openToOpportunities: draft.openToOpportunities === true,
      }),
    };
  } catch {
    return null;
  }
}

export function saveCandidateProfileDraft(
  username: string,
  source: CandidateProfileSource,
  draft: CandidateProfileReviewDraft,
) {
  draftCache.set(
    CANDIDATE_PROFILE_DRAFT_STORAGE_PREFIXES[source] + username,
    draft,
  );
  try {
    sessionStorage.setItem(
      CANDIDATE_PROFILE_DRAFT_STORAGE_PREFIXES[source] + username,
      JSON.stringify(draft),
    );
  } catch {
    // Client navigation can still carry the draft when browser storage is blocked.
  }
}

export function clearCandidateProfileDraft(
  username: string,
  source: CandidateProfileSource,
) {
  draftCache.delete(
    CANDIDATE_PROFILE_DRAFT_STORAGE_PREFIXES[source] + username,
  );
  try {
    sessionStorage.removeItem(
      CANDIDATE_PROFILE_DRAFT_STORAGE_PREFIXES[source] + username,
    );
  } catch {
    // The in-memory draft has already been cleared.
  }
}
